import type { Dapp } from '@src/dto/Dapp';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { AppIcon, popover } from '../portal';

interface AppSwitcherProps {
  dapps: Dapp[];
  selectedDapp: Dapp | null;
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (dapp: Dapp) => void;
  /** Add / Import actions rendered under the app list. */
  actions?: ReactNode;
}

/** Figma "Component 1025": 72px black app switcher with a "YOUR APPS" dropdown. */
export function AppSwitcher({
  dapps,
  selectedDapp,
  isOpen,
  onToggle,
  onSelect,
  actions,
}: AppSwitcherProps) {
  const name =
    selectedDapp?.dappName ??
    (dapps.length === 0 ? 'No apps yet' : 'Select an app');

  return (
    <div className="relative" data-dropdown="apps">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={onToggle}
        className="flex h-[72px] w-full items-center gap-3 rounded-lg bg-black px-2 py-1 text-left transition-colors duration-150 hover:bg-seeker-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-seeker-950"
      >
        <AppIcon
          iconUrl={selectedDapp?.dappIconUrl}
          thumbnailUrl={selectedDapp?.dappIconThumbnailUrl}
          name={selectedDapp?.dappName ?? '?'}
          size={40}
          className={cn(!selectedDapp && 'bg-seeker-900')}
        />
        <span
          title={name}
          className={cn(
            'min-w-0 flex-1 truncate text-sm font-bold leading-5',
            selectedDapp ? 'text-white' : 'text-seeker-400',
          )}
        >
          {name}
        </span>
        <span className="flex size-6 shrink-0 items-center justify-center text-white">
          <ChevronDown
            className={cn(
              'size-4 transition-transform duration-150 ease-out',
              isOpen && 'rotate-180',
            )}
          />
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="apps-menu"
            {...popover}
            aria-label="Your apps"
            className="absolute inset-x-0 top-full z-40 mt-1 flex origin-top flex-col rounded-lg bg-seeker-800 p-2 pt-4 shadow-[0_0_20px_rgba(0,0,0,0.25)]"
          >
            {dapps.length > 0 && (
              <>
                <div className="px-2 pb-2 text-xs font-semibold uppercase leading-4 tracking-[0.01em] text-white/70">
                  Your Apps
                </div>
                <ul className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
                  {dapps.map((dapp) => {
                    const isActive = dapp.id === selectedDapp?.id;
                    return (
                      <li key={dapp.id}>
                        <button
                          type="button"
                          aria-current={isActive ? 'true' : undefined}
                          onClick={() => onSelect(dapp)}
                          className={cn(
                            'flex h-[46px] w-full items-center gap-3 rounded-lg px-2 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70',
                            isActive
                              ? 'bg-seeker-700 text-white'
                              : 'text-white hover:bg-seeker-700/60',
                          )}
                        >
                          <AppIcon
                            iconUrl={dapp.dappIconUrl}
                            thumbnailUrl={dapp.dappIconThumbnailUrl}
                            name={dapp.dappName}
                            size={32}
                          />
                          <span
                            title={dapp.dappName}
                            className="min-w-0 flex-1 truncate text-sm font-bold leading-5"
                          >
                            {dapp.dappName}
                          </span>
                          {isActive && <Check className="size-4 shrink-0" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {actions && <div className="my-2 h-px bg-seeker-700" />}
              </>
            )}
            {actions}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
